/**
 * Development overlay for moving the app's idea of "today".
 *
 * Future sessions, term boundaries, and long histories are otherwise only reachable by waiting
 * for real time to pass. Pinning the clock recomputes every derived screen immediately, so the
 * whole app can be checked against any date.
 *
 * It is deliberately marked in the accent colour and labelled, so it is never mistaken for a
 * feature of the app itself.
 */

import { useState, type ReactNode } from 'react';

import { addDays, today } from '../../shared/dates.js';
import { dayClock, useDayOverride, useToday } from '../clock.js';
import { formatLongDate } from '../ui/format.js';
import { CrossIcon } from '../ui/icons.js';

/** Steps offered either side of the current simulated day. */
const STEPS: readonly { label: string; days: number }[] = [
  { label: '−1 week', days: -7 },
  { label: '−1 day', days: -1 },
  { label: '+1 day', days: 1 },
  { label: '+1 week', days: 7 },
];

/** Renders the collapsed pill and, when opened, the clock controls. */
export function TimeTravel(): ReactNode {
  const day = useToday();
  const override = useDayOverride();
  const [open, setOpen] = useState(false);

  const shortDay = new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`fixed right-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 flex h-9
          items-center gap-1.5 rounded-full border px-2.5 text-tiny font-semibold
          shadow-sm transition-opacity hover:opacity-100 lg:bottom-4
          ${override === null
            ? 'border-line-strong bg-surface text-ink-2 opacity-55'
            : 'border-accent bg-accent text-white opacity-100'}`}
      >
        <span aria-hidden="true">◷</span>
        {shortDay}
      </button>
    );
  }

  return (
    <div
      className="fixed right-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 w-[min(22rem,calc(100vw-1.5rem))]
        rounded-control border-2 border-accent bg-surface p-3 shadow-lg lg:bottom-4"
      role="group"
      aria-label="Development clock"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-tiny font-semibold tracking-wide text-accent-text uppercase">Development clock</p>
          <p className="text-row font-semibold">{formatLongDate(day)}</p>
        </div>
        <button
          type="button"
          aria-label="Close the development clock"
          onClick={() => setOpen(false)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-control text-ink-2 hover:bg-sunken"
        >
          <CrossIcon size={16} />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1.5">
        {STEPS.map((step) => (
          <button
            key={step.label}
            type="button"
            onClick={() => dayClock.setOverride(addDays(day, step.days))}
            className="h-10 rounded-control border border-line-strong text-tiny font-semibold hover:bg-sunken"
          >
            {step.label}
          </button>
        ))}
      </div>

      <label className="mt-3 flex flex-col gap-1 text-tiny font-semibold text-ink-2">
        Jump to a date
        <input
          type="date"
          value={day}
          onChange={(event) => dayClock.setOverride(event.target.value === '' ? null : event.target.value)}
          className="h-10 rounded-control border border-line-strong bg-surface px-2 text-base text-ink"
        />
      </label>

      <button
        type="button"
        onClick={() => dayClock.setOverride(null)}
        disabled={override === null}
        className="mt-3 h-10 w-full rounded-control bg-accent text-tiny font-semibold text-white
          disabled:bg-sunken disabled:text-ink-3"
      >
        {override === null ? `Following the real date (${today()})` : `Back to the real date (${today()})`}
      </button>
    </div>
  );
}
